/**
 * Session Live Notification Component
 * Displays a notification when a session goes live
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"
import { SessionNotificationCard } from "./SessionNotificationCard"

interface SessionLiveNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface SessionLiveNotificationData {
  sessionId?: string
  sessionTitle?: string
  sessionCoverImageUrl?: string | null
  isPaid?: boolean
  fee?: number | null
  duration?: string
  format?: string
}

export function SessionLiveNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: SessionLiveNotificationProps) {
  const data = notification.data as SessionLiveNotificationData

  const sessionTitle = data.sessionTitle || "a session"
  const sessionCoverImageUrl = data.sessionCoverImageUrl
  const isPaid = data.isPaid
  const fee = data.fee
  const duration = data.duration
  const format = data.format
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const formatLabel = format || ""

  const formattedDuration = formatDuration(duration)

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md transition-colors border cursor-pointer ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Brand Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              Your session is now live and open for bookings.
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <SessionNotificationCard
        coverImageUrl={sessionCoverImageUrl}
        title={sessionTitle}
        isPaid={isPaid}
        fee={fee}
        metadata={
          <>
            {formatLabel && (
              <span className="text-white/80 text-[13px] leading-[20px]">{formatLabel}</span>
            )}
            {formattedDuration && (
              <span className="text-white/60 text-[13px] leading-[20px]">{formattedDuration}</span>
            )}
          </>
        }
      />
    </div>
  )
}
